'use client';

import { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';


interface FormData {
  fullName: string;
  email: string;
  inquiryType: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  inquiryType?: string;
  message?: string;
}

const ContactPage = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    inquiryType: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const inquiryTypes = [
    { value: 'media', label: 'Media Inquiry' },
    { value: 'institutional', label: 'Institutional Due Diligence' },
    { value: 'educational', label: 'Educational Inquiry' },
    { value: 'general', label: 'General Professional Inquiry' },
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Please enter your full name';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Professional email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Please provide a detailed message (minimum 20 characters)';
    } else if (formData.message.trim().length > 2000) {
      newErrors.message = 'Message exceeds maximum length (2000 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        inquiryType: '',
        message: '',
      });
    }, 1500);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const getResponseTimeframe = (inquiryType: string): string => {
    switch (inquiryType) {
      case 'media':
        return '24-48 hours';
      case 'institutional':
        return '3-5 business days';
      case 'educational':
        return '5-7 business days';
      case 'general':
        return '7-10 business days';
      default:
        return '7-10 business days';
    }
  };

  if (submitSuccess) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-bg-0 py-24 lg:py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-accent-green rounded-full flex items-center justify-center mx-auto mb-10 shadow-glow-green">
                <Icon name="CheckIcon" size={40} className="text-bg-0" />
              </div>
              <h1 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-8 tracking-tight">
                Inquiry Submitted Successfully
              </h1>
              <p className="font-body text-lg text-text-muted leading-relaxed mb-10">
                Thank you for your professional inquiry. Your message has been received and will be
                reviewed for relevance and professional merit.
              </p>
              <div className="card-premium p-8 mb-10">
                <h2 className="font-headline text-xl font-semibold text-text-primary mb-5">
                  Expected Response Timeframe
                </h2>
                <p className="font-body text-base text-text-muted leading-relaxed">
                  Based on your inquiry type, you can expect a response within{' '}
                  <span className="font-semibold text-accent-green">
                    {getResponseTimeframe(formData.inquiryType)}
                  </span>
                  . Response times may vary based on current commitments and institutional
                  obligations.
                </p>
              </div>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="btn-primary"
              >
                Submit Another Inquiry
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-bg-0 py-20">
        <div className="max-w-content mx-auto px-6">
          <div className="space-y-16">
            {/* Header Section */}
            <div className="text-center max-w-2xl mx-auto space-y-6">
              <h1 className="font-headline text-5xl font-bold text-text-primary tracking-tight">
                Professional Inquiries
              </h1>
              <p className="font-body text-base text-text-muted leading-relaxed">
                For institutional partnerships, media inquiries, educational collaboration, or
                professional opportunities, please complete the form below. All inquiries are
                reviewed for relevance and professional merit.
              </p>
            </div>

            {/* Contact Form */}
            <div className="card-premium p-10 max-w-3xl mx-auto">
              <form onSubmit={handleSubmit} noValidate>
                {/* Full Name */}
                <div className="mb-8">
                  <label
                    htmlFor="fullName"
                    className="font-body text-sm font-semibold text-text-primary mb-3 block"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className={`w-full font-body text-base px-5 py-4 bg-bg-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-green transition-all text-text-primary ${
                      errors.fullName ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && (
                    <p className="font-body text-sm text-red-500 mt-2">{errors.fullName}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-8">
                  <label
                    htmlFor="email"
                    className="font-body text-sm font-semibold text-text-primary mb-3 block"
                  >
                    Professional Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full font-body text-base px-5 py-4 bg-bg-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-green transition-all text-text-primary ${
                      errors.email ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="your.email@company.com"
                  />
                  {errors.email && (
                    <p className="font-body text-sm text-red-500 mt-2">{errors.email}</p>
                  )}
                </div>

                {/* Inquiry Type */}
                <div className="mb-8">
                  <label
                    htmlFor="inquiryType"
                    className="font-body text-sm font-semibold text-text-primary mb-3 block"
                  >
                    Inquiry Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="inquiryType"
                    value={formData.inquiryType}
                    onChange={(e) => handleInputChange('inquiryType', e.target.value)}
                    className={`w-full font-body text-base px-5 py-4 bg-bg-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-green transition-all text-text-primary ${
                      errors.inquiryType ? 'border-red-500' : 'border-border'
                    }`}
                  >
                    <option value="">Select inquiry type</option>
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.inquiryType && (
                    <p className="font-body text-sm text-red-500 mt-2">{errors.inquiryType}</p>
                  )}
                </div>

                {/* Message */}
                <div className="mb-8">
                  <label
                    htmlFor="message"
                    className="font-body text-sm font-semibold text-text-primary mb-3 block"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    rows={6}
                    className={`w-full font-body text-base px-5 py-4 bg-bg-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-green transition-all resize-vertical text-text-primary ${
                      errors.message ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Please provide detailed information about your inquiry..."
                  />
                  <div className="flex justify-between items-center mt-2">
                    {errors.message ? (
                      <p className="font-body text-sm text-red-500">{errors.message}</p>
                    ) : (
                      <p className="font-body text-xs text-text-muted">
                        Minimum 20 characters, maximum 2000 characters
                      </p>
                    )}
                    <p className="font-body text-xs text-text-muted tabular-nums">
                      {formData.message.length}/2000
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>

            {/* Disclaimer */}
            <div className="card-premium p-8 max-w-3xl mx-auto">
              <h2 className="font-headline text-lg font-semibold text-text-primary mb-4">
                Professional Disclaimer
              </h2>
              <p className="font-body text-sm text-text-muted leading-relaxed">
                All inquiries are reviewed for relevance and professional merit. Response times may
                vary based on current commitments and institutional obligations. This contact form
                is intended for professional communications only. For regulatory compliance and
                record-keeping purposes, all correspondence may be archived. By submitting this
                form, you acknowledge that your inquiry is for professional purposes and consent to
                communication via the provided email address.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;